import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { PageHeader, StatusBadge, ApproverRoleBadge, EmptyState } from "./shared/components";
import {
  getPendingApprovals,
  getProcessedApprovals,
  isCurrentUserHr,
  getCurrentEmpNo,
  PendingApprovalItem,
  formatDate,
  prettyStatus,
} from "./services/eSeparation.service";

const TABS: { key: string; label: string }[] = [
  { key: "All", label: "All" },
  { key: "Intimation", label: "Intimation" },
  { key: "ShortNotice", label: "Short Notice" },
  { key: "Withdrawal", label: "Withdrawal" },
  { key: "NoDues", label: "No-Dues" },
];

const PROCESSED_TABS = ["All", "Approved", "Rejected"] as const;

const TYPE_ICONS: Record<string, { icon: string; color: string; bg: string }> = {
  Intimation: { icon: "ti-file-text", color: "#185FA5", bg: "#E6F1FB" },
  ShortNotice: { icon: "ti-clock", color: "#BA7517", bg: "#FFF3EC" },
  Withdrawal: { icon: "ti-arrow-back-up", color: "#0F6E56", bg: "#E1F5EE" },
  NoDues: { icon: "ti-clipboard-check", color: "#993C1D", bg: "#FBE9E2" },
  Termination: { icon: "ti-circle-minus", color: "#C0392B", bg: "#FBE4E0" },
  Death: { icon: "ti-heart-off", color: "#5D6D7E", bg: "#ECEFF1" },
};

const TYPE_LABELS: Record<string, string> = {
  Intimation: "Intimation",
  ShortNotice: "Short Notice",
  Withdrawal: "Withdrawal",
  NoDues: "No-Dues",
  Termination: "Termination",
  Death: "Death Case",
};

const isNoDues = (it: PendingApprovalItem) =>
  it.type === "NoDues" ||
  String(it.request_type || "").toUpperCase().includes("NO_DUES");

// Resolve a request to its tab key directly from the backend request_type so the
// tabs filter strictly by request_type (covers NO_DUES too).
const itemKind = (it: PendingApprovalItem): string => {
  const up = String(it.request_type || it.type || "")
    .toUpperCase()
    .replace(/[\s-]/g, "_");
  if (up.includes("SHORT")) return "ShortNotice";
  if (up.includes("WITHDRAW")) return "Withdrawal";
  if (up.includes("NO_DUES") || up.includes("NODUES")) return "NoDues";
  if (up.includes("RESIGN") || up.includes("INTIMATION")) return "Intimation";
  return it.type || "Intimation";
};

const PRIORITY_COLORS: Record<string, string> = {
  Normal: "#049123",
  Urgent: "#BA7517",
  Overdue: "#FF0F0F",
};

// Human-readable labels for the approver-chain role codes returned by the API.
const ROLE_LABELS: Record<string, string> = {
  AM: "Assistant Manager",
  ASSISTANT_MANAGER: "Assistant Manager",
  CO: "Controlling Officer",
  DY_HOD: "Dy. HOD",
  DEPUTY_HOD: "Dy. HOD",
  DEPUTY: "Deputy",
  HOD: "HOD",
  DIRECTOR: "Director",
  HR: "HR",
  CA: "Competent Authority",
  VIGILANCE: "Vigilance",
  NODAL: "Nodal",
  FINANCE: "Finance",
  MD: "MD",
  // V2: the 4-level HR roster.
  HR_SR_CRA: "HR Assistant",
  HR_AM_MANAGER: "AM / Manager (HR)",
  HR_DY_HOD: "Dy. HOD (HR)",
  HR_HOD: "HOD HR",
};

// Map a role code to one of ApproverRoleBadge's styled keys (falls back to the
// code itself, which renders with the neutral default style).
const ROLE_STYLE_KEY: Record<string, string> = {
  DY_HOD: "Deputy",
  DEPUTY_HOD: "Deputy",
  DEPUTY: "Deputy",
  CO: "CO",
  HOD: "HOD",
  HR: "HR",
  CA: "CA",
  FINANCE: "Finance",
  NODAL: "Nodal",
  VIGILANCE: "Vigilance",
  MD: "MD",
  HR_SR_CRA: "HR",
  HR_AM_MANAGER: "HR",
  HR_DY_HOD: "HR",
  HR_HOD: "HR",
};

const normRole = (r?: string): string =>
  String(r || "")
    .toUpperCase()
    .trim()
    .replace(/[\s.]+/g, "_")
    .replace(/_+/g, "_");

// Categorise a processed request by the approver's own most recent action,
// falling back to the request's overall status.
const processedCategory = (
  item: PendingApprovalItem
): "Approved" | "Rejected" | "Other" => {
  const action = (item.my_action || "").toUpperCase();
  if (action === "APPROVE") return "Approved";
  if (action === "REJECT") return "Rejected";
  if (!action) {
    const status = (item.status || "").toUpperCase();
    if (status === "APPROVED" || status === "CLOSED") return "Approved";
    if (status === "REJECTED") return "Rejected";
  }
  return "Other";
};

const PendingApprovals: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routes = all_routes;
  const roleParam = (location.state as any)?.role;
  // Advisory role passed from the hub; used only for navigation state. The
  // header badge below is derived from the actual per-request approver role.
  const role = roleParam || "Approver";

  const [mainTab, setMainTab] = useState<"Pending" | "Processed">("Pending");
  const [items, setItems] = useState<PendingApprovalItem[]>([]);
  const [processedItems, setProcessedItems] = useState<PendingApprovalItem[]>([]);
  const [tab, setTab] = useState("All");
  const [processedFilter, setProcessedFilter] =
    useState<(typeof PROCESSED_TABS)[number]>("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [isHr, setIsHr] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([getPendingApprovals(role), getProcessedApprovals(role)])
      .then(([pend, proc]) => {
        setItems(pend.data || []);
        setProcessedItems(proc.data || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [role]);

  useEffect(() => {
    isCurrentUserHr(getCurrentEmpNo())
      .then(setIsHr)
      .catch(() => setIsHr(false));
  }, []);

  const matchSearch = (it: PendingApprovalItem) => {
    const q = search.trim().toLowerCase();
    return (
      !q ||
      it.emp_name.toLowerCase().includes(q) ||
      (it.emp_no || "").includes(search) ||
      (it.request_id || "").includes(search)
    );
  };

  const filteredPending = useMemo(
    () =>
      items.filter(
        (it) => (tab === "All" || itemKind(it) === tab) && matchSearch(it)
      ),
    [items, tab, search]
  );

  const filteredProcessed = useMemo(
    () =>
      processedItems.filter(
        (it) =>
          (processedFilter === "All" ||
            processedCategory(it) === processedFilter) &&
          matchSearch(it)
      ),
    [processedItems, processedFilter, search]
  );

  const urgent = items.filter((i) => i.priority === "Urgent").length;
  const overdue = items.filter((i) => i.priority === "Overdue").length;
  const approvedCount = processedItems.filter(
    (i) => processedCategory(i) === "Approved"
  ).length;
  const rejectedCount = processedItems.filter(
    (i) => processedCategory(i) === "Rejected"
  ).length;

  // The inbox is scoped to the logged-in approver, so each pending item's
  // current_level (next_approver_role) is the role this user is acting as.
  // Derive the header badge from the most common role across pending items
  // instead of the hardcoded "HOD".
  const badgeCode = useMemo(() => {
    const counts: Record<string, number> = {};
    items.forEach((it) => {
      const code = normRole(it.current_level);
      if (code && code !== "APPROVER") counts[code] = (counts[code] || 0) + 1;
    });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return top ? top[0] : normRole(role);
  }, [items, role]);

  const badgeLabel = ROLE_LABELS[badgeCode] || (badgeCode ? badgeCode : "Approver");
  const badgeStyleRole = ROLE_STYLE_KEY[badgeCode] || badgeCode || "Approver";

  const isPending = mainTab === "Pending";

  const stats = isPending
    ? [
        { label: "Total Pending", count: items.length, color: "#185FA5" },
        { label: "Urgent", count: urgent, color: "#BA7517" },
        { label: "Overdue", count: overdue, color: "#FF0F0F" },
      ]
    : [
        { label: "Total Processed", count: processedItems.length, color: "#185FA5" },
        { label: "Approved", count: approvedCount, color: "#049123" },
        { label: "Rejected", count: rejectedCount, color: "#FF0F0F" },
      ];

  const openApproval = (it: PendingApprovalItem) => {
    if (isNoDues(it)) {
      if (isHr) {
        navigate(routes.Esepration.noDuesDispatch, {
          state: { request_id: it.request_id },
        });
      } else {
        navigate(
          `${routes.Esepration.noDuesApproverReview}/${it.request_id}`,
          { state: { request_id: it.request_id, emp_no: it.emp_no, role } }
        );
      }
      return;
    }
    // V2 Competent Authority item — a parallel decision, not a step in the
    // sequential chain, but it opens the SAME review screen every other
    // approver uses (ApprovalDetail.tsx detects ca_approval and branches its
    // submit action to the CA-specific endpoint internally).
    if (it.is_ca_item) {
      navigate(`${routes.Esepration.approvals}/${it.request_id}`, {
        state: { request_id: it.request_id, type: it.type, emp_no: it.emp_no, role },
      });
      return;
    }
    // Any HR-level step (V2's 4 levels, or V1's single step) always opens the
    // same HR review screen, regardless of which inbox list got you here.
    // (A withdrawal's steps are plain sequential approvers — it never opens the
    // resignation-specific HR review screen.)
    if (it.current_step_is_hr && it.type !== "Withdrawal") {
      navigate(`/e-sepration/hr-verification/${it.request_id}`, {
        state: { request_id: it.request_id, emp_no: it.emp_no, role: "HR" },
      });
      return;
    }
    navigate(`${routes.Esepration.approvals}/${it.request_id}`, {
      state: { request_id: it.request_id, type: it.type, emp_no: it.emp_no, role },
    });
  };

  const TypeCell: React.FC<{ it: PendingApprovalItem }> = ({ it }) => {
    const tcfg = TYPE_ICONS[it.type] || TYPE_ICONS.Intimation;
    return (
      <span
        className="d-inline-flex align-items-center gap-2"
        style={{ color: tcfg.color, fontWeight: 500 }}
      >
        <span
          className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 30, height: 30, borderRadius: 8, background: tcfg.bg }}
        >
          <i className={`ti ${tcfg.icon}`} />
        </span>
        {TYPE_LABELS[it.type] || it.type}
      </span>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader
          title="Pending Approvals"
          right={
            <ApproverRoleBadge
              role={badgeStyleRole}
              label={`${badgeLabel} — E-Separation`}
            />
          }
          onExit={() => navigate(-1)}
        />

        {/* Main tab segment: Pending / Processed */}
        <div
          className="d-flex p-1 mb-3"
          style={{ background: "rgba(0,0,0,0.04)", borderRadius: 10 }}
        >
          {[
            { key: "Pending", label: `Pending (${items.length})` },
            { key: "Processed", label: `Processed (${processedItems.length})` },
          ].map((t) => (
            <button
              key={t.key}
              className="btn flex-fill fw-semibold"
              style={{
                borderRadius: 8,
                background: mainTab === t.key ? "#fff" : "transparent",
                color: mainTab === t.key ? "#185FA5" : "#6c757d",
                boxShadow: mainTab === t.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              }}
              onClick={() => {
                setMainTab(t.key as "Pending" | "Processed");
                setSearch("");
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Stats */}
        <div className="row g-3 mb-4">
          {stats.map((s) => (
            <div className="col-4" key={s.label}>
              <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
                <div className="card-body text-center">
                  <div className="fs-3 fw-bold" style={{ color: s.color }}>{s.count}</div>
                  <div className="text-muted small">{s.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="card border-0 shadow-sm" style={{ borderRadius: 12 }}>
          <div className="card-body">
            <div className="d-flex gap-2 mb-3">
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <i className="ti ti-search" />
                </span>
                <input
                  className="form-control"
                  placeholder="Search by name, emp no, or request ID"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button className="btn btn-outline-secondary" onClick={load}>
                <i className="ti ti-refresh" />
              </button>
            </div>

            {/* Sub-filters */}
            <div className="d-flex flex-wrap gap-2 mb-3">
              {isPending
                ? TABS.map((t) => (
                    <button
                      key={t.key}
                      className={`btn btn-sm ${tab === t.key ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setTab(t.key)}
                    >
                      {t.label}
                    </button>
                  ))
                : PROCESSED_TABS.map((t) => (
                    <button
                      key={t}
                      className={`btn btn-sm ${processedFilter === t ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setProcessedFilter(t)}
                    >
                      {t}
                    </button>
                  ))}
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : isPending ? (
              filteredPending.length === 0 ? (
                <EmptyState
                  icon="ti-checks"
                  title="No Pending Approvals"
                  message="All E-Separation requests have been processed."
                />
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Type</th>
                        <th>Employee</th>
                        <th>Emp No.</th>
                        <th>Designation</th>
                        <th>Submitted</th>
                        <th>Pending</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPending.map((it, i) => {
                        const pcolor = PRIORITY_COLORS[it.priority] || PRIORITY_COLORS.Normal;
                        return (
                          <tr
                            key={it.request_id || i}
                            style={{ cursor: "pointer" }}
                            onClick={() => openApproval(it)}
                          >
                            <td><TypeCell it={it} /></td>
                            <td className="fw-semibold">{it.emp_name || "—"}</td>
                            <td>{it.emp_no || "—"}</td>
                            <td>{it.designation || "—"}</td>
                            <td>{formatDate(it.submitted_date)}</td>
                            <td>
                              <span className="text-muted">
                                <i className="ti ti-hourglass me-1" />
                                {it.days_pending}d pending
                              </span>
                            </td>
                            <td>
                              <span style={{ color: pcolor }} className="fw-medium">
                                {it.priority}
                              </span>
                            </td>
                            <td>
                              <StatusBadge status={prettyStatus(it.status) || it.current_level} />
                            </td>
                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openApproval(it);
                                }}
                              >
                                Review <i className="ti ti-chevron-right" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            ) : filteredProcessed.length === 0 ? (
              <EmptyState
                icon="ti-archive"
                title="No Processed Requests"
                message="No requests match the selected filters."
              />
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Type</th>
                      <th>Employee</th>
                      <th>Emp No.</th>
                      <th>Designation</th>
                      <th>Submitted</th>
                      <th>Status</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProcessed.map((it, i) => (
                      <tr
                        key={it.request_id || i}
                        style={{ cursor: "pointer" }}
                        onClick={() => openApproval(it)}
                      >
                        <td><TypeCell it={it} /></td>
                        <td className="fw-semibold">{it.emp_name || "—"}</td>
                        <td>{it.emp_no || "—"}</td>
                        <td>{it.designation || "—"}</td>
                        <td>{formatDate(it.submitted_date)}</td>
                        <td>
                          <StatusBadge status={prettyStatus(it.status) || processedCategory(it)} />
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              openApproval(it);
                            }}
                          >
                            View <i className="ti ti-chevron-right" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingApprovals;
